import React from 'react';
import { CheckCircleFilled } from '@ant-design/icons';
import { Tag, Typography } from 'antd';

const { Text } = Typography;

interface RegimenMetric {
  label: string;
  value: React.ReactNode;
}

interface SelectableRegimenCardProps {
  title: string;
  subtitle?: string;
  metrics: RegimenMetric[];
  targetMet?: boolean;
  selected?: boolean;
  targetLabel?: string;
  targetColor?: string;
  onClick?: () => void;
}

const SELECTABLE_REGIMEN_CARD_STYLES = `
  .selectable-regimen-card {
    display: flex;
    align-items: stretch;
    gap: 14px;
    padding: 16px;
    border-radius: 20px;
    border: 1px solid #DCE5EF;
    background: #FFFFFF;
    box-shadow: 0 10px 24px rgba(11, 37, 69, 0.05);
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
    cursor: pointer;
  }

  .selectable-regimen-card--selected {
    border-color: #1677FF;
    box-shadow: 0 12px 32px rgba(22, 119, 255, 0.18);
    transform: translateY(-1px);
  }

  .selectable-regimen-card__check {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 999px;
    border: 2px solid #CDD6E1;
    background: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    color: transparent;
    font-size: 18px;
    transition: all 0.2s ease;
  }

  .selectable-regimen-card--selected .selectable-regimen-card__check {
    border-color: #1677FF;
    background: rgba(22, 119, 255, 0.12);
    color: #1677FF;
  }

  .selectable-regimen-card__content {
    min-width: 0;
    flex: 1;
  }

  .selectable-regimen-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .selectable-regimen-card__title {
    color: #061529;
    font-size: 18px;
    font-weight: 800;
    line-height: 1.15;
  }

  .selectable-regimen-card__subtitle {
    color: #7A869A;
    font-size: 13px;
    line-height: 1.4;
    margin-top: 4px;
  }

  .selectable-regimen-card__tags {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
  }

  .selectable-regimen-card__metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 12px;
  }

  .selectable-regimen-card__metric-label {
    display: block;
    color: #7A869A;
    font-size: 12px;
    line-height: 1.2;
    margin-bottom: 3px;
  }

  .selectable-regimen-card__metric-value {
    color: #061529;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    word-break: break-word;
  }

  @media (max-width: 420px) {
    .selectable-regimen-card {
      padding: 14px;
      gap: 12px;
    }

    .selectable-regimen-card__header {
      flex-direction: column;
      gap: 10px;
    }

    .selectable-regimen-card__tags {
      width: 100%;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: center;
    }

    .selectable-regimen-card__metrics {
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
    }
  }
`;

const SelectableRegimenCard: React.FC<SelectableRegimenCardProps> = ({
  title,
  subtitle,
  metrics,
  targetMet = false,
  selected = false,
  targetLabel,
  targetColor,
  onClick,
}) => (
  <>
    <style>{SELECTABLE_REGIMEN_CARD_STYLES}</style>
    <div
      className={`selectable-regimen-card ${selected ? 'selectable-regimen-card--selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="selectable-regimen-card__check">
        <CheckCircleFilled />
      </div>
      <div className="selectable-regimen-card__content">
        <div className="selectable-regimen-card__header">
          <div style={{ minWidth: 0 }}>
            <div className="selectable-regimen-card__title">{title}</div>
            {subtitle ? <div className="selectable-regimen-card__subtitle">{subtitle}</div> : null}
          </div>
          <div className="selectable-regimen-card__tags">
            <Tag color={targetColor || (targetMet ? 'green' : 'orange')} style={{ margin: 0, borderRadius: 999, paddingInline: 10 }}>
              {targetLabel || (targetMet ? '达标' : '待评估')}
            </Tag>
          </div>
        </div>
        <div className="selectable-regimen-card__metrics">
          {metrics.map((metric) => (
            <div key={metric.label}>
              <Text className="selectable-regimen-card__metric-label">{metric.label}</Text>
              <div className="selectable-regimen-card__metric-value">{metric.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
);

export default SelectableRegimenCard;
